import React, { useState, useEffect } from 'react';
import { Send, Mail, Search, Eye, X, CheckCircle, AlertCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminEmailService, SenderAlias, SentEmail, SendEmailParams } from '../services/adminEmailService';

export const AdminEmailManager: React.FC = () => {
  // Form state
  const [senderAliases, setSenderAliases] = useState<SenderAlias[]>([]);
  const [selectedAlias, setSelectedAlias] = useState<string>('no-reply');
  const [toEmail, setToEmail] = useState<string>('');
  const [senderName, setSenderName] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [htmlBody, setHtmlBody] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  // Sent emails list state
  const [sentEmails, setSentEmails] = useState<SentEmail[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAlias, setFilterAlias] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');

  // View email modal
  const [viewingEmail, setViewingEmail] = useState<SentEmail | null>(null);

  // Load sender aliases on mount
  useEffect(() => {
    loadSenderAliases();
    loadSentEmails();
  }, []);

  // Reload emails when filters or page changes
  useEffect(() => {
    loadSentEmails();
  }, [currentPage, searchTerm, filterAlias, filterDateFrom, filterDateTo]);

  const loadSenderAliases = async () => {
    try {
      const aliases = await adminEmailService.getSenderAliases();
      setSenderAliases(aliases);
      if (aliases.length > 0 && !selectedAlias) {
        setSelectedAlias(aliases[0].alias);
      }
    } catch (error) {
      console.error('Failed to load sender aliases:', error);
    }
  };

  const loadSentEmails = async () => {
    setIsLoadingEmails(true);
    try {
      const result = await adminEmailService.getSentEmails({
        search: searchTerm || undefined,
        sender_alias: filterAlias || undefined,
        dateFrom: filterDateFrom || undefined,
        dateTo: filterDateTo || undefined,
        page: currentPage,
        perPage
      });
      setSentEmails(result.items);
      setTotalItems(result.totalItems);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to load sent emails:', error);
      setSentEmails([]);
    } finally {
      setIsLoadingEmails(false);
    }
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendResult(null);

    if (!toEmail.trim()) {
      setSendResult({ success: false, message: 'Recipient email is required' });
      return;
    }
    if (!subject.trim()) {
      setSendResult({ success: false, message: 'Subject is required' });
      return;
    }
    if (!htmlBody.trim()) {
      setSendResult({ success: false, message: 'Email body is required' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(toEmail.trim())) {
      setSendResult({ success: false, message: 'Please enter a valid email address' });
      return;
    }

    setIsSending(true);
    try {
      const params: SendEmailParams = {
        sender_alias: selectedAlias,
        to_email: toEmail.trim(),
        subject: subject.trim(),
        html_body: wrapHtmlContent(htmlBody),
        sender_name: senderName.trim() || undefined
      };

      const result = await adminEmailService.sendEmail(params);

      if (result.success) {
        setSendResult({ success: true, message: 'Email sent successfully!' });
        // Clear form
        setToEmail('');
        setSubject('');
        setHtmlBody('');
        // Reload sent emails list
        loadSentEmails();
      } else {
        setSendResult({ success: false, message: result.error || 'Failed to send email' });
      }
    } catch (error: any) {
      setSendResult({ success: false, message: error?.message || 'Failed to send email' });
    } finally {
      setIsSending(false);
    }
  };

  // Wrap plain text or simple HTML in a styled email template
  const wrapHtmlContent = (content: string): string => {
    // If content already has html/body tags, return as-is
    if (content.toLowerCase().includes('<html') || content.toLowerCase().includes('<body')) {
      return content;
    }

    // Convert newlines to <br> if content appears to be plain text
    const hasHtmlTags = /<[^>]+>/.test(content);
    const processedContent = hasHtmlTags ? content : content.replace(/\n/g, '<br>');

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    ${processedContent}
    <hr style="border: none; border-top: 1px solid #E5E7EB; margin: 30px 0 20px 0;">
    <p style="color: #6B7280; font-size: 12px; margin: 0;">
      This email was sent from Click and Claim.
    </p>
  </div>
</body>
</html>`;
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterAlias('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            <CheckCircle size={12} className="mr-1" />
            Sent
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
            <AlertCircle size={12} className="mr-1" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            <Clock size={12} className="mr-1" />
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Send Email</h2>
        <p className="text-gray-600 mt-1 text-sm">
          Send emails to users via Brevo API. All sent emails are saved for review.
        </p>
      </div>

      {/* Send Email Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Send size={20} className="mr-2 text-blue-600" />
          Compose Email
        </h3>

        <form onSubmit={handleSendEmail} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sender Alias */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From (Sender email)
              </label>
              <select
                value={selectedAlias}
                onChange={(e) => setSelectedAlias(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {senderAliases.map((alias) => (
                  <option key={alias.alias} value={alias.alias}>
                    {alias.name} &lt;{alias.email}&gt;
                  </option>
                ))}
              </select>
            </div>

            {/* Sender name override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sender name (optional)
              </label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="e.g. Click and Claim, Click and Claim Security"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* To Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To (Recipient Email)
              </label>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Body
            </label>
            <textarea
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              placeholder="Enter your email content here. You can use plain text or HTML."
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Tip: You can use plain text (newlines will be converted to line breaks) or HTML for rich formatting.
            </p>
          </div>

          {/* Result Message */}
          {sendResult && (
            <div className={`p-4 rounded-lg ${sendResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center">
                {sendResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                )}
                <span className={sendResult.success ? 'text-green-800' : 'text-red-800'}>
                  {sendResult.message}
                </span>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSending}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Send Email
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Sent Emails List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Mail size={20} className="mr-2 text-blue-600" />
            Sent Emails
          </h3>

          {/* Filters */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search by email or subject..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sender</label>
              <select
                value={filterAlias}
                onChange={(e) => {
                  setFilterAlias(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">All Senders</option>
                {senderAliases.map((alias) => (
                  <option key={alias.alias} value={alias.alias}>
                    {alias.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => {
                  setFilterDateFrom(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => {
                  setFilterDateTo(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {(searchTerm || filterAlias || filterDateFrom || filterDateTo) && (
            <div className="mt-3">
              <button
                onClick={resetFilters}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <X size={14} className="mr-1" />
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Emails Table */}
        <div className="overflow-x-auto">
          {isLoadingEmails ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : sentEmails.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Mail size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No sent emails found</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sentEmails.map((email) => (
                  <tr key={email.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{email.to_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">{email.subject}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{email.sender_name}</div>
                      <div className="text-xs text-gray-500">{email.sender_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(email.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(email.sent_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setViewingEmail(email)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition"
                        title="View Email"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} emails
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Email Modal */}
      {viewingEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Email Details</h3>
              <button
                onClick={() => setViewingEmail(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                    <p className="text-sm text-gray-900">{viewingEmail.sender_name} &lt;{viewingEmail.sender_email}&gt;</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                    <p className="text-sm text-gray-900">{viewingEmail.to_email}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Sent At</label>
                    <p className="text-sm text-gray-900">{formatDate(viewingEmail.sent_at)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                    {getStatusBadge(viewingEmail.status)}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
                  <p className="text-sm text-gray-900 font-medium">{viewingEmail.subject}</p>
                </div>

                {viewingEmail.brevo_message_id && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Brevo Message ID</label>
                    <p className="text-xs text-gray-500 font-mono">{viewingEmail.brevo_message_id}</p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Email Content</label>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
                    <iframe
                      srcDoc={viewingEmail.html_body}
                      title="Email Preview"
                      className="w-full min-h-[300px] border-0"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setViewingEmail(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
